import { useEffect, useRef, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { productsByCategory } from "@/config/products";
import LeadDialog from "@/components/LeadDialog";

const PLATFORM_DEMO_URL = process.env.REACT_APP_PLATFORM_DEMO_URL || "https://uat.lightningflow.ai";

const primaryLinks = [
  { to: "/platform", label: "Platform" },
  { to: "/security", label: "Security & Trust" },
  { to: "/deployment", label: "Deployment" },
  { to: "/use-cases", label: "Use Cases" },
  { to: "/investors", label: "Company" },
];

const groupedProducts = productsByCategory();

export default function Navigation() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const productsMenuRef = useRef(null);
  const productsButtonRef = useRef(null);
  const mobileButtonRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setProductsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!productsOpen) return undefined;

    const closeOutside = (event) => {
      if (!productsMenuRef.current?.contains(event.target)) {
        setProductsOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setProductsOpen(false);
        productsButtonRef.current?.focus();
      }
    };

    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [productsOpen]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setMobileOpen(false);
        mobileButtonRef.current?.focus();
      }
    };
    const closeOnDesktop = () => {
      if (window.matchMedia("(min-width: 1280px)").matches) setMobileOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("resize", closeOnDesktop);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("resize", closeOnDesktop);
    };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return <header className="sticky top-0 z-50 border-b border-ink-100 bg-white/95 backdrop-blur-xl" data-testid="site-nav">
    <div className="lf-container flex h-20 items-center justify-between">
      <Link to="/" className="lf-nav-logo inline-flex shrink-0 items-center whitespace-nowrap" data-testid="nav-logo-link" aria-label="LIGHTNINGFLOW">
        <img src="/brand/lightningflow-logo-blue-20261019.png" alt="" width="2172" height="724" className="lf-nav-lockup select-none" style={{ filter: "brightness(2.4) saturate(.9)" }} draggable={false} aria-hidden="true" />
      </Link>

      <nav className="hidden xl:flex items-center gap-1 px-2" aria-label="Primary navigation">
        <div className="relative" ref={productsMenuRef}>
          <button ref={productsButtonRef} type="button" onClick={() => setProductsOpen((open) => !open)} className="inline-flex min-h-11 items-center gap-1 px-2 py-2 text-[12px] font-medium text-ink-700 hover:text-ink-900" aria-expanded={productsOpen} aria-controls="nav-products-menu" aria-haspopup="menu" data-testid="nav-products-toggle">
            Products <ChevronDown className={`h-3.5 w-3.5 transition-transform ${productsOpen ? "rotate-180" : ""}`} />
          </button>
          {productsOpen ? <div id="nav-products-menu" role="menu" className="absolute left-0 top-full mt-3 w-[min(680px,calc(100vw-2rem))] max-h-[calc(100dvh-5.5rem)] overflow-y-auto overscroll-contain border border-ink-100 bg-white p-2 shadow-xl" data-testid="nav-products-menu">
            <div className="grid gap-1 md:grid-cols-2">
              {groupedProducts.map((group) => group.items.length ? <div key={group.category} className="py-1">
                <div className="px-3 pt-3 pb-1 font-mono text-[9px] uppercase tracking-[.2em] text-ink-400">{group.label}</div>
                {group.items.map((product) => <Link role="menuitem" key={product.slug} to={`/${product.slug}`} className={`block p-3 ${product.featured ? "border-l-2 border-electric bg-cyan-50/60 hover:bg-cyan-50" : "hover:bg-ink-50"}`} onClick={() => setProductsOpen(false)} data-testid={`nav-product-${product.slug}`} data-featured={product.featured ? "true" : undefined}>
                  <div className="flex items-center gap-2 font-display text-base font-bold text-ink-900">{product.label}{product.featured ? <span className="font-mono text-[9px] font-medium uppercase tracking-[.16em] text-electric">Mac</span> : null}</div>
                  <div className="mt-1 text-sm leading-relaxed text-ink-600">{product.summary}</div>
                </Link>)}
              </div> : null)}
            </div>
          </div> : null}
        </div>
        {primaryLinks.map((link) => <Link key={link.to} to={link.to} aria-current={location.pathname === link.to ? "page" : undefined} className={`px-2 py-2 text-[12px] font-medium transition-colors ${location.pathname === link.to ? "text-ink-900 underline decoration-electric decoration-2 underline-offset-8" : "text-ink-500 hover:text-ink-900"}`}>{link.label}</Link>)}
      </nav>

      <div className="flex items-center gap-2">
        <Link to="/account" className="lf-btn-secondary hidden h-10 whitespace-nowrap px-3 text-[12px] sm:inline-flex xl:px-4 xl:text-[13px]">
          LightningFlow Account
        </Link>
        <button type="button" onClick={() => setDemoOpen(true)} className="lf-btn-primary hidden h-10 whitespace-nowrap px-4 text-[13px] xl:inline-flex" data-testid="nav-demo-link">Request Demo</button>
        <button ref={mobileButtonRef} type="button" className="-mr-2 inline-flex min-h-11 min-w-11 items-center justify-center p-2 xl:hidden" onClick={() => setMobileOpen((open) => !open)} aria-label="Toggle menu" aria-expanded={mobileOpen} aria-controls="nav-mobile-panel" data-testid="nav-mobile-toggle">
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
    </div>

    {mobileOpen ? <div id="nav-mobile-panel" className="xl:hidden max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain border-t border-ink-100 bg-white" data-testid="nav-mobile-panel">
      <nav className="lf-container py-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex flex-col gap-1" aria-label="Mobile navigation">
        {groupedProducts.map((group) => group.items.length ? <div key={group.category} className="flex flex-col">
          <div className="pb-1 pt-3 font-mono text-[10px] uppercase tracking-[.2em] text-ink-400">{group.label}</div>
          {group.items.map((product) => <Link key={product.slug} to={`/${product.slug}`} onClick={closeMobile} aria-current={location.pathname === `/${product.slug}` ? "page" : undefined} className={`block py-2.5 text-base ${product.featured ? "font-bold" : "font-medium"} ${location.pathname === `/${product.slug}` ? "text-ink-900" : "text-ink-700 hover:text-ink-900"}`} data-testid={`nav-mobile-product-${product.slug}`}>{product.label}</Link>)}
        </div> : null)}
        <div className="my-2 border-t border-ink-100" />
        {primaryLinks.map((link) => <Link key={link.to} to={link.to} onClick={closeMobile} aria-current={location.pathname === link.to ? "page" : undefined} className="block py-2.5 text-base text-ink-700 hover:text-ink-900">{link.label}</Link>)}
        <Link to="/account" onClick={closeMobile} className="block py-2.5 text-base font-medium text-ink-900">LightningFlow Account</Link>
        <button type="button" onClick={() => { setMobileOpen(false); setDemoOpen(true); }} className="lf-btn-primary mt-3 w-full">Request LightningFlow Demo</button>
      </nav>
    </div> : null}
    <LeadDialog open={demoOpen} onOpenChange={setDemoOpen} kind="demo" title="See LightningFlow in action" description="Tell us who you are and what you would like to explore." successHref={PLATFORM_DEMO_URL} successLabel="Open the live demo" />
  </header>;
}
